import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { getPreferences, putPreferences } from '../api'
import { readThemeCookie, writeThemeCookie, type ThemePreference } from '../utils/themeCookie'

type ResolvedTheme = 'light' | 'dark'

interface ThemeContextValue {
  theme: ThemePreference
  resolvedTheme: ResolvedTheme
  setTheme: (next: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

// Read the user's selected mode (light/dark/auto). A throw here means a
// component is rendered outside <ThemeProvider> — that's a developer bug,
// fail loud rather than silently default.
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return ctx
}

// Synchronously resolve the OS preference at mount time. matchMedia is the
// canonical signal; if it's missing (very old browsers, jsdom without the
// shim) we fall back to light to keep things safe.
function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

// Compute what should actually be rendered given the user's selected mode.
// 'auto' defers to the OS, 'light'/'dark' are explicit.
function resolveTheme(theme: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (theme === 'light') return 'light'
  if (theme === 'dark') return 'dark'
  return systemPrefersDark ? 'dark' : 'light'
}

// Pick the initial mode synchronously so the very first render already paints
// with the correct palette. Cookie wins when present; otherwise we keep the
// existing dark-only experience as the default for unauthenticated visitors
// without a stored preference.
function getInitialTheme(): ThemePreference {
  const fromCookie = readThemeCookie()
  if (fromCookie) return fromCookie
  return 'dark'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(() => getInitialTheme())
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => getSystemPrefersDark())
  // Live-region message announced to screen readers when the resolved theme
  // changes after first paint. Empty on mount so the initial render doesn't
  // produce a redundant announcement.
  const [announcement, setAnnouncement] = useState('')
  const prevResolvedRef = useRef<ResolvedTheme | null>(null)

  // Tracks whether we've already attempted server hydration this session.
  // Without this guard, an idToken appearing/disappearing during the lifetime
  // of the provider could re-trigger hydration and clobber a fresh setTheme.
  const hydratedRef = useRef(false)

  const resolvedTheme: ResolvedTheme = resolveTheme(theme, systemPrefersDark)

  // Apply the resolved theme to <html>. The CSS already reacts to .dark via
  // global.css, including color-scheme so native form controls and scrollbars
  // flip too. We only toggle the class — never mutate inline styles — so the
  // CSS rules stay in charge of the actual palette. Skip the initial pass for
  // the live-region announcement so SR users do not hear "Dark theme applied"
  // on every page load.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (resolvedTheme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    if (prevResolvedRef.current !== null && prevResolvedRef.current !== resolvedTheme) {
      setAnnouncement(`${resolvedTheme === 'dark' ? 'Dark' : 'Light'} theme applied`)
    }
    prevResolvedRef.current = resolvedTheme
  }, [resolvedTheme])

  // Subscribe to OS theme changes only while the user is on 'auto'. When they
  // pick an explicit mode, the listener is torn down so we don't waste cycles
  // reacting to a signal we'd ignore anyway.
  useEffect(() => {
    if (theme !== 'auto') return
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches)
    }

    // Sync once on subscribe in case the OS preference flipped between the
    // initial state read and this effect running.
    setSystemPrefersDark(mql.matches)

    // addEventListener is the modern API; addListener is the Safari <14
    // fallback. We branch defensively because some test shims only expose
    // one or the other.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler)
      return () => mql.removeEventListener('change', handler)
    }
    if (typeof (mql as MediaQueryList & { addListener?: (h: (e: MediaQueryListEvent) => void) => void }).addListener === 'function') {
      const legacy = mql as MediaQueryList & {
        addListener: (h: (e: MediaQueryListEvent) => void) => void
        removeListener: (h: (e: MediaQueryListEvent) => void) => void
      }
      legacy.addListener(handler)
      return () => legacy.removeListener(handler)
    }
    return undefined
  }, [theme])

  // Hydrate from the server once on mount, but only when we have an idToken
  // (matches the existing auth signal in src/api/index.ts). The cookie has
  // already supplied an initial value, so this is a no-flicker upgrade: if
  // the server says something different we update state and rewrite the
  // cookie so the next session starts with the right value immediately.
  useEffect(() => {
    if (hydratedRef.current) return
    if (typeof window === 'undefined') return

    const idToken = window.localStorage.getItem('idToken')
    if (!idToken) return

    hydratedRef.current = true
    let cancelled = false
    void (async () => {
      try {
        const { theme: serverTheme } = await getPreferences()
        if (cancelled) return
        // Only overwrite when the server actually disagrees with the cookie.
        // Avoids a redundant cookie write and an unnecessary state update.
        const current = readThemeCookie()
        if (serverTheme !== current) {
          writeThemeCookie(serverTheme)
          setThemeState(serverTheme)
        }
      } catch {
        // getPreferences swallows errors and returns a default, but guard
        // anyway so a future change to the API client cannot break mount.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  // setTheme is the only public mutation path. We update local state first so
  // the UI reflects the choice immediately, write the cookie so a refresh
  // keeps the new value, and fire-and-forget the server sync. Server failures
  // log a warning but never revert local state — the cookie wins for UX
  // continuity, the server reconciles next session.
  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next)
    writeThemeCookie(next)

    if (typeof window === 'undefined') return
    const idToken = window.localStorage.getItem('idToken')
    if (!idToken) return

    void putPreferences(next).catch((err) => {
      console.warn('Failed to persist theme preference:', err)
    })
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
      {/* Off-screen live region. Visually hidden but assistive tech reads
          updates so users know the theme actually flipped. Polite priority
          avoids interrupting in-progress speech. */}
      <span
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {announcement}
      </span>
    </ThemeContext.Provider>
  )
}
